import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { combineLatest, Observable, of, startWith } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import {
  FinanceService,
  NewHireProvisionRequest,
  NewHireProvisionResult,
  ProjectDetailsRow,
  RateCardAutocompleteSuggestions,
} from '../../finance.service';
import { ToasterService } from '@app/shared/services/toaster.service';

export interface NewHireModalData {
  employeeId: string;
  resourceName?: string;
}

const HIRING_STATUS_OPTIONS = ['JOINED', 'HOLD', 'BACKED_OUT'];
const CAPEX_OPEX_OPTIONS = ['CAPEX', 'PROJ_OPEX', 'RTB_OPEX'];
const PO_TYPE_OPTIONS = ['Billable', 'Non-Billable'];
const TIER_OPTIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'];
const RATE_CARD_MATCH_OPTIONS = ['Match', 'Mismatch', 'Pending'];

/**
 * New Hire Form modal — fires from Resource Info's Edit pencil when the
 * row's hiring status is "New Hire". 3-column grid of 16 editable fields
 * per Figma; project-derived attributes (Product Suite / Agile Team /
 * D&T Invoice Approver / Activity / Category) aren't on the form
 * because they live on FinanceProject and surface via the listing
 * joins, not via this form's input.
 */
@Component({
  selector: 'app-new-hire-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
  templateUrl: './new-hire-modal.component.html',
  styleUrl: './new-hire-modal.component.scss',
})
export class NewHireModalComponent implements OnInit {
  readonly hiringStatusOptions = HIRING_STATUS_OPTIONS;
  readonly capexOpexOptions = CAPEX_OPEX_OPTIONS;
  readonly poTypeOptions = PO_TYPE_OPTIONS;
  readonly tierOptions = TIER_OPTIONS;
  readonly rateCardMatchOptions = RATE_CARD_MATCH_OPTIONS;

  form!: FormGroup;
  saving = false;
  errorMessage: string | null = null;

  /** Project picker source. Loaded once on init via listProjectDetails
   *  (which returns full project metadata) so we can auto-populate
   *  project-derived fields when the operator picks a project. */
  projectOptions: ProjectDetailsRow[] = [];
  filteredProjects$!: Observable<ProjectDetailsRow[]>;

  /** Distinct existing rate-card field values — same pattern as the Add
   *  Project modal's suggestions stream. shareReplay so each filter
   *  stream gets the cached value. */
  private suggestions$!: Observable<RateCardAutocompleteSuggestions>;
  filteredVendorRateCards$!: Observable<string[]>;
  filteredGlBusinessUnits$!: Observable<string[]>;
  filteredBillToFus$!: Observable<string[]>;
  filteredBillToMos$!: Observable<string[]>;
  filteredGlAccounts$!: Observable<string[]>;
  filteredInvoiceLocations$!: Observable<string[]>;

  // Static-option fields converted from mat-select to autocomplete so
  // every field-with-options on the form has the same search-icon
  // treatment — no more mixed dropdown / autocomplete visuals.
  filteredHiringStatuses$!: Observable<string[]>;
  filteredCapexOpex$!: Observable<string[]>;
  filteredPoTypes$!: Observable<string[]>;
  filteredTierModels$!: Observable<string[]>;
  filteredRateCardMatches$!: Observable<string[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NewHireModalData,
    private readonly dialogRef: MatDialogRef<NewHireModalComponent, NewHireProvisionResult | null>,
    private readonly fb: FormBuilder,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      // Assignment-level
      projectId: [null as number | null, [Validators.required]],
      // Plain text mirror of the picker — the autocomplete writes the
      // user-typed string here, projectId is set when the user picks a
      // suggestion.
      projectLabel: [''],
      hiringStatus: ['JOINED', [Validators.required]],
      capexOpex: [''],
      allocationPct: [null as number | null],
      poType: [''],
      billingStartDate: [null as Date | null, [Validators.required]],
      billingEndDate: [null as Date | null],
      // Rate card
      tierModel: [''],
      dntCost: [null as number | null],
      fullHrAmount: [null as number | null],
      vendorRateCard: [''],
      rateCardMatch: [''],
      glBusinessUnit: [''],
      billToFu: [''],
      billToMo: [''],
      glAccount: [''],
      invoiceLocation: [''],
    });

    // Load full project metadata (not just FinanceProjectOption) so the
    // form can auto-populate GL BU / Bill to FU / Bill to MO from the
    // picked project. size=500 covers any realistic catalog without
    // pagination plumbing in the modal.
    this.financeService.listProjectDetails({ size: 500 }).subscribe({
      next: (page) => (this.projectOptions = page?.rows ?? []),
      error: () => (this.projectOptions = []),
    });

    const projectLabel = this.form.get('projectLabel');
    if (projectLabel) {
      this.filteredProjects$ = projectLabel.valueChanges.pipe(
        startWith(''),
        map((typed) => (typed ?? '').toString().trim().toLowerCase()),
        map((needle) => this.filterProjects(needle)),
      );
    }

    // Rate-card autocomplete suggestions — seeded with empty arrays so
    // the filter streams emit immediately even before the HTTP call
    // resolves; the real values land transparently when it does.
    const EMPTY: RateCardAutocompleteSuggestions = {
      vendorRateCards: [],
      glBusinessUnits: [],
      billToFus: [],
      billToMos: [],
      glAccounts: [],
      invoiceLocations: [],
    };
    this.suggestions$ = this.financeService.getRateCardAutocompleteSuggestions().pipe(
      catchError(() => of(EMPTY)),
      startWith(EMPTY),
      shareReplay(1),
    );
    this.filteredVendorRateCards$ = this.makeFilterStream(
      'vendorRateCard',
      this.suggestions$.pipe(map((s) => s.vendorRateCards)),
    );
    this.filteredGlBusinessUnits$ = this.makeFilterStream(
      'glBusinessUnit',
      this.suggestions$.pipe(map((s) => s.glBusinessUnits)),
    );
    this.filteredBillToFus$ = this.makeFilterStream(
      'billToFu',
      this.suggestions$.pipe(map((s) => s.billToFus)),
    );
    this.filteredBillToMos$ = this.makeFilterStream(
      'billToMo',
      this.suggestions$.pipe(map((s) => s.billToMos)),
    );
    this.filteredGlAccounts$ = this.makeFilterStream(
      'glAccount',
      this.suggestions$.pipe(map((s) => s.glAccounts)),
    );
    this.filteredInvoiceLocations$ = this.makeFilterStream(
      'invoiceLocation',
      this.suggestions$.pipe(map((s) => s.invoiceLocations)),
    );

    // Static-option autocompletes — same makeFilterStream pattern fed
    // by a synchronous of(...) so dropdowns + autocompletes don't
    // diverge visually (every option-bearing field uses the same input
    // + search-icon look).
    this.filteredHiringStatuses$ = this.makeFilterStream(
      'hiringStatus',
      of(this.hiringStatusOptions),
    );
    this.filteredCapexOpex$ = this.makeFilterStream(
      'capexOpex',
      of(this.capexOpexOptions),
    );
    this.filteredPoTypes$ = this.makeFilterStream(
      'poType',
      of(this.poTypeOptions),
    );
    this.filteredTierModels$ = this.makeFilterStream(
      'tierModel',
      of(this.tierOptions),
    );
    this.filteredRateCardMatches$ = this.makeFilterStream(
      'rateCardMatch',
      of(this.rateCardMatchOptions),
    );
  }

  /** combineLatest of value + options for race-safe filtering, same
   *  pattern as the Add Project modal. */
  private makeFilterStream(
    controlName: string,
    options$: Observable<string[]>,
  ): Observable<string[]> {
    const control = this.form.get(controlName);
    if (!control) {
      throw new Error(`Form control "${controlName}" missing`);
    }
    const value$ = control.valueChanges.pipe(startWith(control.value ?? ''));
    return combineLatest([value$, options$]).pipe(
      map(([typed, opts]) => {
        const needle = (typed ?? '').toString().trim().toLowerCase();
        return needle ? opts.filter((o) => o.toLowerCase().includes(needle)) : opts.slice();
      }),
    );
  }

  private filterProjects(needle: string): ProjectDetailsRow[] {
    if (!needle) return this.projectOptions.slice(0, 100);
    return this.projectOptions
      .filter(
        (p) =>
          (p.projectName ?? '').toLowerCase().includes(needle) ||
          (p.projectIdCode ?? '').toLowerCase().includes(needle),
      )
      .slice(0, 100);
  }

  /** Display function for the project autocomplete input — shows the
   *  human-readable label even though the underlying form control holds
   *  the typed string. */
  displayProject = (value: string | null): string => value ?? '';

  /** Called when the operator picks a project. Stores the project's id +
   *  label for the BE payload, then patches the form with the project's
   *  pre-existing GL BU / Bill to FU / Bill to MO so the operator doesn't
   *  have to retype them. Only patches fields that are currently empty
   *  so the operator's prior input isn't clobbered. */
  onProjectPicked(opt: ProjectDetailsRow): void {
    const patch: Record<string, unknown> = {
      projectId: opt.projectId,
      projectLabel: this.formatProjectLabel(opt),
    };
    const v = this.form.value;
    if (!v.glBusinessUnit && opt.glBusinessUnit) patch['glBusinessUnit'] = opt.glBusinessUnit;
    if (!v.billToFu && opt.billToFu) patch['billToFu'] = opt.billToFu;
    if (!v.billToMo && opt.billToMo) patch['billToMo'] = opt.billToMo;
    this.form.patchValue(patch);
  }

  private formatProjectLabel(opt: ProjectDetailsRow): string {
    const name = opt.projectName ?? '';
    const code = opt.projectIdCode ?? '';
    if (name && code) return `${name} | ${code}`;
    return name || code;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    if (this.form.invalid || this.form.value.projectId == null) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Please fill the required fields (Project, Hiring Status, Start Date).';
      return;
    }
    this.saving = true;
    this.errorMessage = null;

    const v = this.form.value;
    const payload: NewHireProvisionRequest = {
      projectId: v.projectId,
      hiringStatus: v.hiringStatus,
      capexOpex: v.capexOpex || undefined,
      allocationPct: v.allocationPct ?? undefined,
      poType: v.poType || undefined,
      billingStartDate: toIsoDate(v.billingStartDate),
      billingEndDate: toIsoDateOrUndefined(v.billingEndDate),
      tierModel: v.tierModel || undefined,
      dntCost: v.dntCost ?? undefined,
      fullHrAmount: v.fullHrAmount ?? undefined,
      vendorRateCard: v.vendorRateCard || undefined,
      rateCardMatch: v.rateCardMatch || undefined,
      glBusinessUnit: v.glBusinessUnit || undefined,
      billToFu: v.billToFu || undefined,
      billToMo: v.billToMo || undefined,
      glAccount: v.glAccount || undefined,
      invoiceLocation: v.invoiceLocation || undefined,
    };

    this.financeService.provisionNewHire(this.data.employeeId, payload).pipe(
      catchError((err) => {
        this.saving = false;
        const beMessage =
          err?.error?.message || err?.error?.error || err?.message || null;
        this.errorMessage =
          err?.status === 400 && beMessage
            ? beMessage
            : 'Failed to provision new hire. Please try again.';
        return of(null);
      }),
    ).subscribe((result) => {
      if (!result) return;
      this.saving = false;
      this.toaster.showSuccess(
        `${this.data.resourceName ?? 'Resource'} provisioned.`,
        'New Hire',
      );
      this.dialogRef.close(result);
    });
  }
}

/** ISO yyyy-MM-dd serialiser — Material datepicker returns Date objects. */
function toIsoDate(d: Date | null): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toIsoDateOrUndefined(d: Date | null): string | undefined {
  return d ? toIsoDate(d) : undefined;
}
